<template>
    <div v-if="filter.hasOwnProperty('action')" class="d-flex">
        <v-select v-model="modelValue['action']"
                  :items="operations"
                  class="w-50"
                  clearable
                  density="compact" item-title="description" item-value="abbr" label="Action"
                  @update:model-value="focusValueInput"/>
        <v-text-field ref="valueInput" v-model="modelValue['data']" :label="filter.label" :type="filter.type?? 'text'" class="w-50"
                      density="compact"/>
    </div>
    <div v-else class="d-flex">
        <template v-if="filter.type === 'select'">
            <v-select v-model="modelValue['data']" :item-title="filter.title" :item-value="filter.value"
                      :items="filter.source" :label="filter.label" :multiple="filter.multiple" clearable
                      density="compact"></v-select>
        </template>
        <template v-else-if="filter.type === 'autocomplete'">
            <v-autocomplete v-model="modelValue['data']" :custom-filter="matchesSearchKeys"
                            :item-title="filter.title" :item-value="filter.value" :items="filter.source"
                            :label="filter.label" :multiple="filter.multiple" clearable density="compact">
                <template #item="{ props: itemProps, item }">
                    <v-list-item v-bind="itemProps" :subtitle="secondaryLine(item.raw)"/>
                </template>
            </v-autocomplete>
        </template>
        <template v-else>
            <v-text-field v-show="filter.type !== 'hidden'" v-model="modelValue['data']" :label="filter.label"
                          :type="filter.type" class="w-50" density="compact"/>
            <template v-if="index === 'geo_radius'">
                <v-text-field v-model="modelValue['latitude']" class="w-50" density="compact" label="Latitude"
                              type="number"/>
                <v-text-field v-model="modelValue['longitude']" class="w-50" density="compact" label="Longitude"
                              type="number"/>
                <v-btn class="text-success" flat icon="mdi-map-marker" ripple @click="showMapDialog"/>
            </template>
        </template>
    </div>

    <Map-Dialog v-if="mapDialogSwitch" v-model="mapDialogSwitch" :setMapValues="mapValues" @mapValue="mapValue"/>
</template>
<script setup>
import {computed, nextTick, ref, watch} from "vue";
import MapDialog from "@/components/cards/devices/MapDialog.vue";

const props = defineProps({
    modelValue: Object,
    filter: Object,
    index: String,
});

// Autocomplete filters can be searched on more than the displayed title. A Go
// account's company_name is just the owner's personal name, and several read
// exactly the same, so the email and account code are what tell them apart.
const searchKeys = computed(() => props.filter.searchKeys ?? [props.filter.title]);

const matchesSearchKeys = (_value, query, item) => {
    const needle = (query ?? '').trim().toLowerCase();
    if (!needle) return true;
    const row = item?.raw ?? {};
    return searchKeys.value.some((key) => String(row[key] ?? '').toLowerCase().includes(needle));
};

// Show the non-title search fields under the name so the right row is pickable
// by eye, not just findable by typing.
const secondaryLine = (row) => searchKeys.value
    .filter((key) => key !== props.filter.title)
    .map((key) => row?.[key])
    .filter(Boolean)
    .join('  |  ');

const isNumericFilter = computed(() => {
    const type = props.filter.type;
    return type === 'number' || type === 'datetime-local';
});

// Only serial_num accepts 'like' on the API (DeviceFilterRequest /
// AssetFilterRequest rules), and FilterHelpers' like arm searches devices.slno
// whatever the key. Every other action-able text key (imei, iccid) rejected a
// defaulted 'like' with a 422 ("Devices could not be loaded"), so they default
// to 'eq', the only operator that means anything for an identifier.
const LIKE_KEYS = new Set(['serial_num']);
const allowsLike = computed(() => !isNumericFilter.value && LIKE_KEYS.has(props.index));

watch(() => props.modelValue['data'], (val) => {
    if (val && !props.modelValue['action'] && props.filter.hasOwnProperty('action')) {
        props.modelValue['action'] = allowsLike.value ? 'like' : 'eq';
    }
});
const valueInput = ref(null);
const focusValueInput = () => {
    nextTick(() => {
        const el = valueInput.value?.$el ?? valueInput.value;
        const input = el?.querySelector('input');
        if (input) input.focus();
    });
};
const mapDialogSwitch = ref(false);
const mapValues = ref({
    type: 0,
    center_lat: null,
    center_lon: null,
    radius: null,
})
const comparisonOps = [
    {abbr: "eq", symbol: "=", description: "Equal to"},
    {abbr: "lt", symbol: "<", description: "Less than"},
    {abbr: "gt", symbol: ">", description: "Greater than"},
    {abbr: "lte", symbol: "<=", description: "Less than & Equal to"},
    {abbr: "gte", symbol: ">=", description: "Greater than & Equal to"},
];
const operations = computed(() => {
    return allowsLike.value
        ? [{abbr: "like", symbol: "%", description: "Like"}, ...comparisonOps]
        : comparisonOps;
});
const showMapDialog = () => {
    mapDialogSwitch.value = true;
    mapValues.value = {
        type: 0,
        center_lat: props.modelValue["latitude"],
        center_lon: props.modelValue["longitude"],
        radius: props.modelValue["data"],
    };
}
const mapValue = (template) => {
    props.modelValue["latitude"] = template.center_lat;
    props.modelValue["longitude"] = template.center_lon;
    props.modelValue["data"] = template.radius;
}
</script>
